<template>
    <div v-if="config" class="alert-config-form">
        <LaBaseForm
            :form-data="formData"
            :vuex-action-mutation="formAction"
            button="Save"
            id="alert-config"
            buttonPortalTo="modalFooter"
        >
            <p class="description">
                {{ config.description }} Waiting alerts are sent together once the oldest is older than the aggregation
                window; 0 sends right away.
            </p>

            <LaBaseCheckbox v-model="enabled" name="enabled" label="Enabled" data-test="alert-enabled" />

            <LaBaseInput
                v-model="channel"
                name="channel"
                label="Slack channel"
                :placeholder="`empty = default channel${defaultChannel ? ` (${defaultChannel})` : ''}`"
                data-test="alert-channel"
            />

            <LaBaseInput
                v-model="aggregationSeconds"
                name="aggregationSeconds"
                label="Aggregation (s)"
                placeholder="0 = send immediately"
                data-test="alert-aggregation"
                :rules="rules.aggregationSeconds"
            />

            <template v-for="(field, key) in settingsSchema" :key="key">
                <LaBaseCheckbox
                    v-if="field.type === 'bool'"
                    v-model="settings[key]"
                    :name="`setting-${key}`"
                    :label="field.label"
                    :data-test="`alert-setting-${key}`"
                />
                <LaBaseInput
                    v-else
                    v-model="settings[key]"
                    :name="`setting-${key}`"
                    :label="field.label"
                    :placeholder="`default ${field.default}`"
                    :data-test="`alert-setting-${key}`"
                    :rules="[isWholeNumberBetween(field.min ?? 0, field.max ?? 1000000)]"
                />
            </template>

            <LaBaseInput
                v-model="titleTemplate"
                name="titleTemplate"
                label="Title template"
                data-test="alert-title-template"
                :error="preview?.errors?.title_template"
            />

            <LaBaseTextarea
                v-model="itemTemplate"
                name="itemTemplate"
                label="Line template"
                data-test="alert-item-template"
                :rows="3"
                :error="preview?.errors?.item_template"
            />

            <p class="hint">
                Jinja2. The title gets <code>count</code>. The line template renders one line per alert (identical lines
                are merged with ×N) and gets the alert's data:
                <template v-if="preview?.variables?.length">
                    <code v-for="variable in preview.variables" :key="variable" class="variable">{{ variable }}</code>
                </template>
                <span v-else>(no alerts of this type yet)</span>
            </p>

            <LaSlackPreview :blocks="preview?.blocks">
                <template #label>
                    Preview
                    <span v-if="preview">
                        over {{ preview.alert_count }} {{ preview.alert_count === 1 ? "alert" : "alerts" }}
                    </span>
                </template>
            </LaSlackPreview>
        </LaBaseForm>
    </div>
</template>

<script>
import LaBaseForm from "@/components/BaseForm";
import LaBaseInput from "@/components/inputs/BaseInput";
import LaBaseTextarea from "@/components/inputs/BaseTextarea";
import LaBaseCheckbox from "@/components/inputs/BaseCheckbox";
import LaSlackPreview from "@/components/alerts/SlackPreview";
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import store from "@/store/store";
import { isWholeNumberBetween } from "@/helpers/rules";
import { getterTypes as dialogGetterTypes } from "@/store/Dialog/types";
import { actionTypes as alertActionTypes, getterTypes as alertGetterTypes } from "@/store/Alerts/types";

const PREVIEW_DELAY_MS = 400;

export default {
    name: "LaAlertConfigForm",
    components: {
        LaBaseForm,
        LaBaseInput,
        LaBaseTextarea,
        LaBaseCheckbox,
        LaSlackPreview,
    },
    setup() {
        const config = computed(() => store.getters[dialogGetterTypes.getDialog].item);
        const defaultChannel = computed(() => store.getters[alertGetterTypes.getDefaultChannel]);

        const enabled = ref(!!config.value?.enabled);
        const channel = ref(config.value?.channel || "");
        const aggregationSeconds = ref(String(config.value?.aggregation_seconds ?? 300));
        const titleTemplate = ref(config.value?.title_template || "");
        const itemTemplate = ref(config.value?.item_template || "");

        // The type's thresholds, rendered from its schema - ints edited as text
        const settingsSchema = config.value?.settings_schema || {};
        const settings = reactive(
            Object.fromEntries(
                Object.entries(settingsSchema).map(([key, field]) => {
                    const value = config.value.settings?.[key] ?? field.default;
                    return [key, field.type === "bool" ? !!value : String(value)];
                })
            )
        );

        const formData = computed(() => ({
            id: config.value?.id,
            enabled: enabled.value,
            channel: channel.value.trim(),
            aggregation_seconds: parseInt(aggregationSeconds.value, 10),
            title_template: titleTemplate.value,
            item_template: itemTemplate.value,
            settings: Object.fromEntries(
                Object.entries(settingsSchema).map(([key, field]) => [
                    key,
                    field.type === "bool" ? settings[key] : parseInt(settings[key], 10),
                ])
            ),
        }));

        // Preview of the unsaved templates, re-rendered by the API as they are typed
        const preview = ref(null);
        let previewTimer = null;
        let previewRequest = 0;
        const loadPreview = () => {
            if (!config.value) return;
            const request = ++previewRequest;
            store
                .dispatch(alertActionTypes.preview, {
                    code: config.value.code,
                    title_template: titleTemplate.value,
                    item_template: itemTemplate.value,
                })
                .then((data) => {
                    // an older, slower response must not overwrite a newer one
                    if (request === previewRequest) preview.value = data;
                })
                .catch(() => {});
        };
        watch([titleTemplate, itemTemplate], () => {
            clearTimeout(previewTimer);
            previewTimer = setTimeout(loadPreview, PREVIEW_DELAY_MS);
        });
        onBeforeUnmount(() => clearTimeout(previewTimer));
        loadPreview();

        const rules = {
            aggregationSeconds: [isWholeNumberBetween(0, 86400)],
        };

        return {
            config,
            defaultChannel,
            enabled,
            channel,
            aggregationSeconds,
            titleTemplate,
            itemTemplate,
            settingsSchema,
            settings,
            isWholeNumberBetween,
            formData,
            formAction: alertActionTypes.updateConfig,
            preview,
            rules,
        };
    },
};
</script>

<style lang="scss" scoped>
.description,
.hint {
    font-size: 0.85rem;
    opacity: 0.8;
    margin-bottom: 0.8rem;
}
.variable {
    margin-right: 0.3rem;
}
</style>
